import React, { useState } from 'react';

/**
 * MatchingPuzzle — Drag locations to match with riddles/clues.
 *
 * data.pairs is an array of:
 *   { riddle: "The clue text...", answer: "Location Name" }
 */

export default function MatchingPuzzle({ data, onSolved }) {
  const pairs = data.pairs;

  // Track which answer is placed in which slot (by riddle index)
  const [placements, setPlacements] = useState({});
  const [results, setResults] = useState(null); // null | 'checking' | { correct: [], wrong: [] }
  const [finished, setFinished] = useState(false);
  const [draggedAnswer, setDraggedAnswer] = useState(null);

  // Get all answers, shuffled once on mount
  const [shuffledAnswers] = useState(() => {
    const answers = pairs.map((p) => p.answer);
    return answers.sort(() => Math.random() - 0.5);
  });

  // Answers that haven't been placed yet
  const availableAnswers = shuffledAnswers.filter(
    (ans) => !Object.values(placements).includes(ans)
  );

  const handleDragStart = (answer) => {
    setDraggedAnswer(answer);
  };

  const handleDrop = (riddleIndex) => {
    if (draggedAnswer && !finished) {
      setPlacements((prev) => ({ ...prev, [riddleIndex]: draggedAnswer }));
      setDraggedAnswer(null);
      setResults(null);
    }
  };

  const handleRemove = (riddleIndex) => {
    if (finished) return;
    setPlacements((prev) => {
      const updated = { ...prev };
      delete updated[riddleIndex];
      return updated;
    });
    setResults(null);
  };

  const handleSubmit = () => {
    const correct = [];
    const wrong = [];

    pairs.forEach((pair, i) => {
      if (placements[i] === pair.answer) {
        correct.push(i);
      } else {
        wrong.push(i);
      }
    });

    setResults({ correct, wrong });

    if (wrong.length === 0) {
      setFinished(true);
      setTimeout(onSolved, 1200);
    }
  };

  const allPlaced = Object.keys(placements).length === pairs.length;

  return (
    <div className="puzzle-matching">
      <p className="puzzle-prompt">{data.prompt || "Match each memory to its place!"}</p>

      <div className="matching-grid">
        {/* Riddles / Clues */}
        <div className="matching-riddles">
          {pairs.map((pair, i) => {
            const isCorrect = results?.correct?.includes(i);
            const isWrong = results?.wrong?.includes(i);
            const placed = placements[i];

            return (
              <div
                key={i}
                className={`matching-row ${isCorrect ? 'match-correct' : ''} ${isWrong ? 'match-wrong' : ''}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(i)}
              >
                <div className="matching-riddle">{pair.riddle}</div>
                <div className="matching-arrow">→</div>
                <div
                  className={`matching-slot ${placed ? 'slot-filled' : 'slot-empty'}`}
                  onClick={() => placed && handleRemove(i)}
                >
                  {placed || 'Drop here...'}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Available answers to drag */}
      {!finished && (
        <div className="matching-answers">
          <p className="matching-answers-label">Drag these to match:</p>
          <div className="matching-answer-chips">
            {availableAnswers.map((ans) => (
              <div
                key={ans}
                className="matching-answer"
                draggable
                onDragStart={() => handleDragStart(ans)}
              >
                {ans}
              </div>
            ))}
          </div>
        </div>
      )}

      {!finished && (
        <button
          className="puzzle-submit"
          onClick={handleSubmit}
          disabled={!allPlaced}
        >
          Check my matches!
        </button>
      )}

      {results?.wrong?.length > 0 && (
        <p className="puzzle-wrong">Some matches aren't quite right... try again! 💭</p>
      )}

      {finished && (
        <div className="story-done">
          <span className="story-done-text">Perfect matches! 💛</span>
        </div>
      )}
    </div>
  );
}
